import React from 'react';
import styled from "styled-components";
import { Button, Tooltip, tooltipClasses } from "@mui/material";

const Div = styled.div`
  flex-grow: 1;

  .bqn-button {
    width: 100%;
    padding: 7px;
    height: 100px;
    min-width: 80px;

    &.big {
      height: 180px;
    }

    .content {
      display: flex;
      gap: 10px;
      flex-direction: column;
      width: 100%;
      height: 100%;
      justify-content: space-between;

      .content-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: wrap;
      }

      .bqn-row {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      label {
        cursor: pointer;
      }

      .bqn-id {
        font-family: "AllianceNo2-SemiBold";
        font-size: 14px;
        color: var(--Text-Primary);
      }

      .function {
        text-transform: lowercase;
      }

      .bqn-property {
        font-size: 10px;
        color: var(--Text-Primary);
        line-height: 8px;
        text-transform: capitalize;
      }

      .hostname {
        font-size: 10px;
        color: var(--Text-Primary);
        line-height: 10px;
        text-transform: none;
      }

      .nSubscribers {
        font-size: 12px;
        color: var(--Text-Primary);
        line-height: 12px;
        text-transform: none;
        margin-top: 15px;
      }

      .ip-address {
        user-select: text;
      }

      .alarm {
        display: flex;
        align-items: end;
        gap: 6px;
        flex-wrap: wrap;

        .beacon {
          width: 10px;
          height: 10px;
          border-radius: 5px;
        }
      }

      .online-status {
        display: flex;
        align-items: end;
        gap: 3px;
        flex-wrap: wrap;

        .online-icon {
          width: 13px;
          height: 13px;

          i {
            margin-left: -2px;
            font-size: 14px;
            color: var(--signal-green, #00ba07);
          }
        }
      }

      .login-icon {
        width: 13px;
        height: 13px;

        i {
          font-size: 14px;
          color: #747474;
        }
      }
    }

    &.normal {
      border: 1px solid var(--signal-green, #00ba07);
      background-color: var(--signal-green-light, #f5fff5) !important;

      .beacon {
        background-color: var(--signal-green, #00ba07);
      }
    }

    &.normal:hover {
      border: 1px solid var(--signal-green-hover, #00ba07);
      background-color: var(--signal-green-light-hover, #f5fff5) !important;
    }

    &.warning {
      border: 1px solid var(--signal-yellow, #baa700);
      background-color: var(--signal-yellow-light, #fff8e5) !important;

      .beacon {
        background-color: var(--signal-yellow, #baa700);
      }
    }

    &.warning:hover {
      border: 1px solid var(--signal-yellow-hover, #baa700);
      background-color: var(--signal-yellow-light-hover, #fff8e5) !important;
    }

    &.critical {
      border: 1px solid var(--signal-red, #ba0000);
      background-color: var(--signal-red-light, #fff5f5) !important;

      .beacon {
        background-color: var(--signal-red, #ba0000);
      }
    }

    &.critical:hover {
      border: 1px solid var(--signal-red-hover, #ba0000);
      background-color: var(--signal-red-light-hover, #fff5f5) !important;
    }

    &.offline {
      .online-icon i {
        color: var(--signal-red, #ba0000) !important;
      }
    }
  }
`;

const CustomizedTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "transparent",
    maxWidth: 250,

    "#dbTooltip": {
      padding: 15,
    },
  },
}));

export function BqnButton({ bqn, onClick, alarms, big, login, nSubscribers }) {
  const levels = alarms[bqn.bqnId]
    ? alarms[bqn.bqnId].map((alarm) => alarm.level)
    : [];

  const status =
    bqn.function === "offline"
      ? "offline"
      : levels.includes("critical")
      ? "critical"
      : levels.includes("warning")
      ? "warning"
      : "normal";

  const TooltipTitle = (
    <div id="dbTooltip">
      <table>
        <tbody>
          {alarms[bqn.bqnId] &&
            alarms[bqn.bqnId].map(({ alarm, level }) => (
              <tr key={`${bqn.bqnId}${alarm}`}>
                <td> {alarm}</td>
                <td className="alarm-value">
                  <div className={`alarm-ball ${level}`}></div>
                  {level}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Div>
      <Button
        variant="outlined"
        onClick={onClick}
        className={`bqn-button ${status}${big ? " big" : ""}`}
        disabled={bqn.function === "offline"}
      >
        <div className="content">
          <div className="content-row">
            <div className="bqn-property ip-address"> {bqn.ipAddress} </div>
            <div>
              {login && (
                <div className="login-icon">
                  <i className="material-icons">person</i>
                </div>
              )}
            </div>
          </div>
          <div className="bqn-row">
            <div className="bqn-id"> BQN {bqn.bqnId}</div>
            <div className="hostname"> {bqn.hostname} </div>
            <div className="nSubscribers"> {nSubscribers} subs </div>
          </div>
          <div className="content-row">
            <CustomizedTooltip title={TooltipTitle}>
              <div className="alarm">
                <div className="beacon"></div>
                <div className="bqn-property">
                  {status !== "offline" ? status : ""}
                </div>
              </div>
            </CustomizedTooltip>
            <div className="online-status">
              <div className="online-icon">
                <i className="material-icons">power_settings_new</i>
              </div>
              <div className="bqn-property"> {bqn.function} </div>
            </div>
          </div>
        </div>
      </Button>
    </Div>
  );
}
